import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Plus, 
  Calendar, 
  Trash2, 
  ExternalLink,
  Loader2,
  ListChecks,
  ArrowRight,
  Sparkles,
  Zap,
  UtensilsCrossed,
  FileText,
  ChevronRight,
  Store
} from 'lucide-react';
import { format } from 'date-fns';

// Mock grocery list for demo mode (defined outside component to avoid recreation)
const MOCK_DEMO_LIST = {
  id: 'demo-list-1',
  name: 'Weekly Groceries',
  created_at: new Date().toISOString(),
  source: 'meal_plan',
  source_details: '3 planned meals',
  items: [
    { name: 'Chicken breast', amount: 2, unit: 'lbs', aisle: 'Meat', checked: false },
    { name: 'Pasta', amount: 1, unit: 'box', aisle: 'Pantry', checked: false },
    { name: 'Garlic', amount: 4, unit: 'cloves', aisle: 'Produce', checked: false },
    { name: 'Olive oil', amount: 1, unit: 'bottle', aisle: 'Pantry', checked: false },
    { name: 'Parmesan cheese', amount: 1, unit: 'cup', aisle: 'Dairy', checked: false },
    { name: 'Breadcrumbs', amount: 1, unit: 'cup', aisle: 'Pantry', checked: false },
    { name: 'Fresh basil', amount: 1, unit: 'bunch', aisle: 'Produce', checked: false },
    { name: 'Tomatoes', amount: 4, unit: '', aisle: 'Produce', checked: false },
  ]
};

// Helper to get source description
const getSourceDescription = (list) => {
  if (list.source === 'meal_plan') {
    return list.source_details || 'Created from meal plan';
  } else if (list.source === 'recipe') {
    return `From ${list.source_details || 'recipe'}`;
  } else if (list.name?.includes('Ingredients')) {
    return `From ${list.name.replace(' Ingredients', '')}`;
  }
  return 'Manual list';
};

const GroceryLists = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDemoMode = searchParams.get('demo') === 'true';
  
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generatingFromPlan, setGeneratingFromPlan] = useState(false);
  
  // Demo mode state
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [demoListGenerated, setDemoListGenerated] = useState(false);

  const loadLists = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/grocery-lists');
      setLists(response.data);
    } catch (error) {
      console.error('Failed to load grocery lists:', error);
      toast.error('Failed to load grocery lists');
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  // Auto-generate list in demo mode if coming from meal planner
  useEffect(() => {
    const shouldGenerate = searchParams.get('generated') === 'true';
    if (isDemoMode && !isAuthenticated && shouldGenerate && !demoListGenerated) {
      setDemoListGenerated(true);
      setLists([MOCK_DEMO_LIST]);
    }
  }, [isDemoMode, isAuthenticated, searchParams, demoListGenerated]);

  const createNewList = async () => {
    if (!newListName.trim()) {
      toast.error('Please enter a list name');
      return;
    }

    // Demo mode - show signup modal
    if (isDemoMode && !isAuthenticated) {
      setShowSignupModal(true);
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/grocery-lists', {
        name: newListName.trim(),
        items: []
      });
      setLists([response.data, ...lists]);
      setNewListName('');
      setDialogOpen(false);
      toast.success('Grocery list created');
      navigate(`/grocery-lists/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create grocery list');
    } finally {
      setCreating(false);
    }
  };

  const generateFromMealPlan = async () => {
    // Demo mode - generate mock list
    if (isDemoMode && !isAuthenticated) {
      setGeneratingFromPlan(true);
      // Simulate generation delay
      await new Promise(resolve => setTimeout(resolve, 800));
      setDemoListGenerated(true);
      setLists([MOCK_DEMO_LIST]);
      setGeneratingFromPlan(false);
      toast.success('Grocery list generated from meal plan!');
      return;
    }

    setGeneratingFromPlan(true);
    try {
      const response = await api.post('/grocery-lists/from-meal-plan');
      setLists([response.data, ...lists]);
      toast.success('Grocery list generated from meal plan!');
      navigate(`/grocery-lists/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate grocery list';
      toast.error(message);
    } finally {
      setGeneratingFromPlan(false);
    }
  };

  const deleteList = async (listId, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Demo mode
    if (isDemoMode && !isAuthenticated) {
      setLists(lists.filter(l => l.id !== listId));
      toast.success('Grocery list deleted');
      return;
    }

    try {
      await api.delete(`/grocery-lists/${listId}`);
      setLists(lists.filter(l => l.id !== listId));
      toast.success('Grocery list deleted');
    } catch (error) {
      toast.error('Failed to delete grocery list');
    }
  };

  const handleListClick = (listId, e) => {
    if (isDemoMode && !isAuthenticated) {
      e.preventDefault();
      navigate(`/grocery-lists/${listId}?demo=true`);
    }
  };

  return (
    <div className="min-h-screen py-8" data-testid="grocery-lists-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Demo Mode Step 3 Banner */}
        {isDemoMode && !isAuthenticated && (
          <Card className="border-primary/50 bg-gradient-to-r from-primary/10 to-accent/5 mb-6" data-testid="demo-step3-banner">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-primary">Step 3</Badge>
                    <span className="font-semibold text-foreground">Generate your grocery list automatically</span>
                  </div>
                  <span className="text-sm text-muted-foreground ml-[52px]">We&apos;ll combine ingredients from your meal plan into one clean list.</span>
                </div>
                <span className="text-xs text-muted-foreground">No duplicates. No guesswork.</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-2">
              Grocery Lists
            </h1>
            <p className="text-muted-foreground">
              Create lists from your meal plan or build one manually — export when you&apos;re ready.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={generateFromMealPlan}
              disabled={generatingFromPlan}
              data-testid="generate-from-plan-btn"
            >
              {generatingFromPlan ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Calendar className="h-4 w-4 mr-2" />
              )}
              From Meal Plan
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-list-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New List
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display']">Create Grocery List</DialogTitle>
                  <DialogDescription>
                    Give your grocery list a name to get started
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Input
                    placeholder="e.g., Weekly Groceries, Party Supplies"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createNewList()}
                    data-testid="new-list-name-input"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createNewList} disabled={creating} data-testid="confirm-create-btn">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Lists */}
        {loading && isAuthenticated ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="border-border/50 animate-pulse">
                <CardHeader>
                  <div className="h-5 bg-muted rounded w-2/3" />
                  <div className="h-4 bg-muted rounded w-1/3 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : lists.length > 0 ? (
          <>
            {/* Success State Message for Demo */}
            {isDemoMode && !isAuthenticated && demoListGenerated && (
              <Card className="border-accent/50 bg-accent/5 mb-6" data-testid="demo-success-state">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-accent-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Your grocery list is ready</p>
                      <p className="text-sm text-muted-foreground">Ingredients are automatically combined and organized for easy shopping.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {lists.map((list) => {
                const checkedCount = list.items?.filter(i => i.checked).length || 0;
                const totalCount = list.items?.length || 0;
                const progressPercent = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;
                const isComplete = checkedCount === totalCount && totalCount > 0;
                
                return (
                <Link 
                  key={list.id} 
                  to={isDemoMode && !isAuthenticated ? `/grocery-lists/${list.id}?demo=true` : `/grocery-lists/${list.id}`}
                  onClick={(e) => handleListClick(list.id, e)}
                  data-testid={`list-${list.id}`}
                >
                  <Card className="border-border/50 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer group h-full">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="font-['Playfair_Display'] group-hover:text-primary transition-colors truncate">
                            {list.name}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <span>{format(new Date(list.created_at), 'MMM d, yyyy')}</span>
                            <span className="text-muted-foreground/50">•</span>
                            <span className="text-xs flex items-center gap-1">
                              {list.source === 'meal_plan' || list.source_details ? (
                                <><UtensilsCrossed className="h-3 w-3" /> {getSourceDescription(list)}</>
                              ) : list.name?.includes('Ingredients') ? (
                                <><UtensilsCrossed className="h-3 w-3" /> {getSourceDescription(list)}</>
                              ) : (
                                <><FileText className="h-3 w-3" /> Manual list</>
                              )}
                            </span>
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                          onClick={(e) => deleteList(list.id, e)}
                          data-testid={`delete-list-${list.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Progress bar */}
                      <Progress value={progressPercent} className="h-1.5" />
                      
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2 text-sm">
                            <ListChecks className="h-4 w-4 text-muted-foreground" />
                            <span className={isComplete ? 'text-primary font-medium' : 'text-muted-foreground'}>
                              {checkedCount} / {totalCount} items checked
                            </span>
                            {isComplete && <Badge className="bg-primary/20 text-primary border-0 text-xs">Done!</Badge>}
                          </div>
                          {!isComplete && totalCount > 0 && (
                            <span className="text-xs text-muted-foreground ml-6">
                              Tap to start shopping
                            </span>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )})}
            </div>

            {/* Step 4 Export Callout */}
            {isDemoMode && !isAuthenticated && demoListGenerated && (
              <Card className="border-accent/30 bg-gradient-to-r from-accent/5 to-primary/5 mt-6" data-testid="demo-step4-callout">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="border-accent">Step 4</Badge>
                        <span className="font-semibold text-foreground">Shop smarter</span>
                      </div>
                      <span className="text-sm text-muted-foreground ml-[52px]">Send this list directly to Instacart or your favorite grocery store.</span>
                    </div>
                    <Link to={`/grocery-lists/${lists[0]?.id}?demo=true`}>
                      <Button size="sm" variant="outline" data-testid="demo-view-list-cta">
                        View List
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* Demo Mode Empty State */
          isDemoMode && !isAuthenticated ? (
            <Card className="border-border/50" data-testid="demo-empty-state">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <ShoppingCart className="h-10 w-10 text-primary/50" />
                </div>
                <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-2">Your grocery list is ready to be created</h3>
                <p className="text-muted-foreground text-center mb-2 max-w-md">
                  Generate a list based on the meals you&apos;ve planned.
                </p>
                <p className="text-xs text-muted-foreground mb-6">Takes less than a second.</p>
                <Button onClick={generateFromMealPlan} disabled={generatingFromPlan} size="lg" data-testid="demo-generate-list-btn">
                  {generatingFromPlan ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Generate Grocery List
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/50" data-testid="empty-state">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <ShoppingCart className="h-10 w-10 text-primary/50" />
                </div>
                <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-2">Your grocery list is one click away</h3>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  Plan a few meals and we&apos;ll build your list automatically — or create one manually.
                </p>
                <div className="flex gap-3">
                  <Button onClick={generateFromMealPlan} disabled={generatingFromPlan} data-testid="empty-generate-btn">
                    <Calendar className="h-4 w-4 mr-2" />
                    From Meal Plan
                  </Button>
                  <Button variant="outline" onClick={() => setDialogOpen(true)} data-testid="empty-create-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New List
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        )}

        {/* Subtle Export Teaser - shown for free users with lists */}
        {isAuthenticated && lists.length > 0 && (
          <div className="mt-8 text-center" data-testid="export-teaser">
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Store className="h-4 w-4" />
              Export lists to Instacart and your favorite stores with Pro.
            </p>
          </div>
        )}

        {/* Demo Signup Modal */}
        <Dialog open={showSignupModal} onOpenChange={setShowSignupModal}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-xl">
                Save your grocery list
              </DialogTitle>
              <DialogDescription>
                Create a free account to keep your lists and continue shopping.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-4">
              <Link to="/auth" className="w-full">
                <Button className="w-full" size="lg" data-testid="demo-signup-modal-cta">
                  Create free account
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                onClick={() => setShowSignupModal(false)}
                data-testid="demo-continue-browsing"
              >
                Continue browsing
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default GroceryLists;
